import React, { useEffect, useRef } from "react";
import { initAOS } from "../../helpers/aosConfig";

import groupPhoto from "../../assets/joyride_group_light.webp";
import teemuKuvitus from "../../assets/teemu-kuvitus.webp";

const highlights = [
  "Laaja ohjelmisto",
  "Näyttävät valot",
  "Tilannetajua"
];

const descriptionParagraphs = [
  "Joyride on rockhenkinen bilebändi Keski-Uudeltamaalta. Soitamme häissä, yksityisjuhlissa ja yritystapahtumissa. Bändissä yhdistyvät pitkän linjan keikkakokemus ja musiikillinen koulutus – hyvällä soundilla ja rennolla otteella.",
  "Ohjelmistomme kattaa kotimaiset hitit, kansainväliset bileklassikot, rockin ja popin sekä tanssi- ja hääkappaleet. Rakennamme setit juhlan ja yleisön mukaan, rauhallisista hetkistä vauhdikkaisiin bileisiin.",
  "Näyttävät valot, tilannetaju ja sujuva toteutus kuuluvat kokonaisuuteen. Luemme yleisöä ja mukautamme tunnelmaa illan edetessä. Me huolehdimme esiintymisestä, jotta sinä voit keskittyä juhlimiseen."
];

const MeetTheBand = () => {
  const headingRef = useRef(null);

  useEffect(() => {
    initAOS();

    const heading = headingRef.current;

    // Pelkkä komponentin latautuminen ei tarkoita osion katsomista.
    if (!heading || typeof window.IntersectionObserver !== "function") {
      return;
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (
          !entry ||
          !entry.isIntersecting ||
          entry.intersectionRatio < 0.5
        ) {
          return;
        }

        // Lähetetään tapahtuma, kun vähintään puolet otsikosta näkyy.
        if (typeof window.gaEvent === "function") {
          window.gaEvent("meista_katsottu");
        }

        // Sama osio mitataan vain kerran tämän näyttökerran aikana.
        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    observer.observe(heading);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="meista"
      aria-labelledby="meista-heading"
      className="relative w-full overflow-hidden bg-gray-100 py-12 md:py-20 dark:bg-joyrideNavy"
    >
      {/* Taustan värivalot */}
      <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-joyrideCyan/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-joyridePurple/25 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid items-start gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
          {/* Teksti tulee mobiilissa ennen kuvia. */}
          <div>
            <p
              className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-joyridePurple dark:text-joyrideCyan"
              data-aos="fade-up"
            >
              Tutustu Joyrideen
            </p>

            {/* Sivun nykyinen H1-otsikko säilytetään. */}
            <h1
              ref={headingRef}
              id="meista-heading"
              className="mb-5 max-w-3xl font-lobster text-3xl font-bold leading-tight text-joyridePurple drop-shadow-sm sm:text-4xl md:text-5xl dark:text-joyrideCyan dark:text-shadow-lg"
              data-aos="fade-up"
            >
              Joyride – rockhenkinen bilebändi
            </h1>

            <div
              className="mb-5 flex flex-wrap gap-2"
              data-aos="fade-up"
            >
              {highlights.map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-joyridePurple/20 bg-white/90 px-3 py-2 text-sm font-bold text-joyrideNavy shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-white/10 dark:text-joyrideLight"
                >
                  {item}
                </span>
              ))}
            </div>

            {/* Oma esittelyteksti ilman palautteeseen viittaavaa lainausmerkkiä. */}
            <div
              className="rounded-3xl border border-black/5 bg-white/75 p-5 shadow-xl backdrop-blur-sm md:p-6 dark:border-white/10 dark:bg-white/10"
              data-aos="fade-up"
            >
              {descriptionParagraphs.map((text) => (
                <p
                  key={text}
                  className="mb-4 text-base leading-relaxed text-joyrideNavy last:mb-0 md:text-lg dark:text-joyrideLight"
                >
                  {text}
                </p>
              ))}
            </div>
          </div>

          {/* Kuvat: mobiilissa tekstin alla, tietokoneella oikealla. */}
          <div
            className="relative flex flex-col"
            data-aos="fade-up"
          >
            <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-joyrideCyan/25 via-transparent to-joyridePurple/25 blur-2xl" />

            <div className="relative overflow-hidden rounded-[2rem] bg-white p-2 shadow-2xl ring-1 ring-black/10 dark:bg-white/10 dark:ring-white/10">
              <img
                src={groupPhoto}
                alt="Bilebändi Joyriden jäsenet"
                className="h-auto w-full rounded-[1.5rem]"
                loading="lazy"
                width="900"
                height="600"
              />
            </div>

            {/* Kuvitus säilyy suurilla näytöillä, mutta aiempaa matalampana. */}
            <div className="relative mt-4 hidden overflow-hidden rounded-[2rem] bg-white p-2 shadow-xl ring-1 ring-black/10 lg:block dark:bg-white/10 dark:ring-white/10">
              <img
                src={teemuKuvitus}
                alt="Joyride esiintymässä live-keikalla"
                className="h-72 w-full rounded-[1.5rem] object-cover object-center xl:h-80"
                loading="lazy"
                width="700"
                height="1200"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetTheBand;